import { useEffect, useMemo, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { api, formatApiError, formatINR } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  ArrowRight, MapPin, Package, Weight, Calendar, IndianRupee, Truck,
  Send, Gavel, Check, MessageSquare, ArrowLeft, UserRound,
} from "lucide-react";

export default function LoadDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [load, setLoad] = useState(null);
  const [bids, setBids] = useState([]);
  const [selectedBidId, setSelectedBidId] = useState(null);

  const isDriverKind = load?.kind === "driver_hire";
  const isSender = user && user.role === "sender" && load && load.sender_id === user.id;
  const canProviderBid = user && (
    (load?.kind === "consignment" && user.role === "lorry_owner") ||
    (load?.kind === "driver_hire" && user.role === "driver")
  );
  const myBid = useMemo(() => bids.find((b) => b.provider_id === user?.id), [bids, user]);
  const accent = isDriverKind ? "#FFD700" : "#FF5A00";

  const fetchAll = async () => {
    try {
      const [{ data: l }, { data: b }] = await Promise.all([
        api.get(`/loads/${id}`),
        api.get(`/bids?load_id=${id}`),
      ]);
      setLoad(l.load);
      setBids(b.bids);
      if (!selectedBidId) {
        const target = b.bids.find(x => x.status === "accepted") || b.bids[0];
        if (target) setSelectedBidId(target.id);
      }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  useEffect(() => { fetchAll(); }, [id]); // eslint-disable-line

  if (!load) return <div className="min-h-screen bg-[#0A0A0A] text-white p-10">Loading load…</div>;

  const selectedBid = bids.find((b) => b.id === selectedBidId) || (canProviderBid ? myBid : null);

  const acceptBid = async (bidId) => {
    try { await api.post(`/bids/${bidId}/accept`); toast.success("Bid accepted. Load confirmed."); fetchAll(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const updateStatus = async (status) => {
    try { await api.post(`/loads/${load.id}/status`, { status }); toast.success(`Marked ${status.replace("_", " ")}`); fetchAll(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const backTo = user?.role === "sender" ? "/sender" :
    user?.role === "lorry_owner" ? "/owner" :
    user?.role === "driver" ? "/driver" :
    user?.role === "admin" ? "/admin" : "/";

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 py-8">
        <Link to={backTo} className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-white" data-testid="load-back-link">
          <ArrowLeft className="w-4 h-4" /> Back
        </Link>

        <div className="mt-6 grid lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 p-6 bg-[#141414] border border-[#2A2A2A]">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <div className="overline" style={{ color: accent }}>
                  {isDriverKind ? "DRIVER HIRE" : "CONSIGNMENT"} #{load.id.slice(0, 8).toUpperCase()}
                </div>
                <h1 className="font-display font-black text-3xl mt-2 tracking-tight" data-testid="load-title">{load.title}</h1>
              </div>
              <StatusBadge status={load.status} testId="load-status" />
            </div>

            <div className="mt-6 flex items-center gap-3 font-mono-ll text-lg">
              <MapPin className="w-4 h-4" style={{ color: accent }} />
              <span>{load.source_city}</span>
              <ArrowRight className="w-5 h-5" style={{ color: accent }} />
              <span>{load.destination_city}</span>
            </div>

            <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3">
              {isDriverKind ? (
                <KV icon={UserRound} label="Commodity" value={load.commodity_type || "—"} />
              ) : (
                <KV icon={Package} label="Material" value={load.material || "—"} />
              )}
              {isDriverKind ? (
                <KV icon={Calendar} label="Trip" value={`${load.trip_duration_days || 0} day(s)`} />
              ) : (
                <KV icon={Weight} label="Weight" value={`${load.weight_kg} kg`} />
              )}
              <KV icon={Truck} label="Vehicle" value={load.vehicle_type} />
              <KV icon={Calendar} label={isDriverKind ? "Start" : "Pickup"} value={new Date(load.pickup_date).toLocaleDateString("en-IN")} />
            </div>

            {load.notes && (
              <div className="mt-4 p-3 bg-[#0A0A0A] border border-[#2A2A2A] text-sm text-zinc-300">
                <div className="overline text-zinc-500 mb-1">Notes</div>{load.notes}
              </div>
            )}
          </div>

          <div className="p-6 bg-[#141414] border border-[#2A2A2A]">
            <div className="overline text-zinc-500">Sender&apos;s Budget</div>
            <div className="font-display font-black text-4xl mt-2 tracking-tight">{formatINR(load.budget_inr)}</div>
            {load.final_price_inr && (
              <>
                <div className="mt-6 overline" style={{ color: accent }}>Locked-in price</div>
                <div className="font-display font-black text-3xl mt-1" style={{ color: accent }}>{formatINR(load.final_price_inr)}</div>
              </>
            )}

            {canProviderBid && !myBid && ["posted", "bidding"].includes(load.status) && (
              <PlaceBidDialog loadId={load.id} accent={accent} onDone={fetchAll} />
            )}

            {load.status === "confirmed" && (isSender || (user && load.accepted_provider_id === user.id)) && (
              <Button onClick={() => updateStatus("in_transit")} data-testid="mark-in-transit-btn"
                className="mt-6 w-full rounded-sm bg-[#FFD700] hover:bg-[#FFE24E] text-black font-bold">Mark In-Transit</Button>
            )}
            {load.status === "in_transit" && (isSender || (user && load.accepted_provider_id === user.id)) && (
              <Button onClick={() => updateStatus("delivered")} data-testid="mark-delivered-btn"
                className="mt-3 w-full rounded-sm bg-emerald-500 hover:bg-emerald-400 text-black font-bold">Mark Delivered</Button>
            )}
          </div>
        </div>

        <div className="mt-8 grid lg:grid-cols-12 gap-4">
          <div className="lg:col-span-5 p-4 bg-[#141414] border border-[#2A2A2A]">
            <div className="flex items-center justify-between mb-3">
              <div className="overline text-zinc-400 flex items-center gap-2"><Gavel className="w-4 h-4" style={{ color: accent }} /> Bids ({bids.length})</div>
            </div>
            {bids.length === 0 && (
              <div className="p-6 text-center text-zinc-500 border border-dashed border-[#2A2A2A]">No bids yet.</div>
            )}
            <div className="space-y-2 max-h-[520px] overflow-y-auto">
              {bids.map((b) => (
                <BidRow key={b.id} bid={b} accent={accent} active={selectedBidId === b.id}
                  onSelect={() => setSelectedBidId(b.id)}
                  onAccept={isSender ? () => acceptBid(b.id) : null}
                  canAccept={isSender && ["posted", "bidding"].includes(load.status)}
                  isMe={b.provider_id === user?.id} />
              ))}
            </div>
          </div>

          <div className="lg:col-span-7">
            {selectedBid ? (
              <NegotiationPanel bid={selectedBid} load={load} accent={accent} onUpdated={fetchAll} />
            ) : (
              <div className="h-full min-h-[400px] flex items-center justify-center p-10 bg-[#141414] border border-[#2A2A2A] text-zinc-500">
                {canProviderBid ? "Place a bid to start chatting with the sender." : "Select a bid to view chat & negotiate."}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function KV({ icon: I, label, value }) {
  return (
    <div>
      <div className="flex items-center gap-2 overline text-zinc-500"><I className="w-3 h-3" /> {label}</div>
      <div className="mt-1 font-semibold">{value}</div>
    </div>
  );
}

function BidRow({ bid, active, onSelect, onAccept, canAccept, isMe, accent }) {
  const info = bid.provider_info || {};
  const detail = bid.provider_role === "driver"
    ? `${info.driver_profile?.home_city || "—"} · ${info.driver_profile?.experience_years || 0}y exp`
    : `${info.vehicle?.type || "Vehicle"} · ${info.vehicle?.number || "—"}`;

  return (
    <div onClick={onSelect} data-testid={`bid-row-${bid.id}`}
      className={`p-3 cursor-pointer border transition-colors ${active ? "bg-[color:var(--acc)]/5" : "bg-[#0A0A0A] hover:border-zinc-500"}`}
      style={active ? { borderColor: accent } : { borderColor: "#2A2A2A" }}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="font-semibold text-sm">
            {bid.provider_name}
            {isMe && <span className="ml-2 text-[10px] uppercase tracking-wider" style={{ color: accent }}>You</span>}
          </div>
          <div className="text-xs text-zinc-500 font-mono-ll mt-0.5">{detail}</div>
        </div>
        <StatusBadge status={bid.status} />
      </div>
      <div className="mt-3 flex items-center justify-between">
        <div>
          <div className="font-display font-black text-2xl">{formatINR(bid.price_inr)}</div>
          <div className="text-xs text-zinc-500">ETA {bid.eta_hours}h · last by {bid.last_offer_by}</div>
        </div>
        {canAccept && bid.status === "open" && onAccept && (
          <Button size="sm" onClick={(e) => { e.stopPropagation(); onAccept(); }}
            data-testid={`accept-bid-${bid.id}`} className="rounded-sm text-black font-bold"
            style={{ backgroundColor: accent }}>
            <Check className="w-4 h-4 mr-1" /> Accept
          </Button>
        )}
      </div>
    </div>
  );
}

function NegotiationPanel({ bid, load, onUpdated, accent }) {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [counter, setCounter] = useState("");
  const [counterOpen, setCounterOpen] = useState(false);
  const scrollRef = useRef(null);

  const fetchMsgs = async () => {
    try { const { data } = await api.get(`/messages?bid_id=${bid.id}`); setMessages(data.messages); }
    catch (e) { /* ignore */ }
  };
  useEffect(() => {
    fetchMsgs();
    const t = setInterval(fetchMsgs, 4000);
    return () => clearInterval(t);
  }, [bid.id]); // eslint-disable-line

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    try { await api.post("/messages", { bid_id: bid.id, text: text.trim() }); setText(""); fetchMsgs(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const sendCounter = async () => {
    const price = Number(counter);
    if (!price || price <= 0) return toast.error("Enter a valid price");
    try {
      await api.post(`/bids/${bid.id}/counter`, { price_inr: price, note: "" });
      toast.success("Counter-offer sent");
      setCounter(""); setCounterOpen(false); fetchMsgs(); onUpdated?.();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const canAct = bid.status === "open" && ["posted", "bidding"].includes(load.status);
  const canParticipate = user && [bid.sender_id, bid.provider_id].includes(user.id);

  return (
    <div className="bg-[#141414] border border-[#2A2A2A] flex flex-col h-[560px]">
      <div className="p-4 border-b border-[#2A2A2A] flex items-center justify-between">
        <div>
          <div className="overline text-zinc-500 flex items-center gap-2"><MessageSquare className="w-4 h-4" style={{ color: accent }} /> Negotiation</div>
          <div className="mt-1 font-semibold text-sm">
            {bid.provider_name} · Current offer <span style={{ color: accent }}>{formatINR(bid.price_inr)}</span>
          </div>
        </div>
        {canAct && canParticipate && (
          <Dialog open={counterOpen} onOpenChange={setCounterOpen}>
            <DialogTrigger asChild>
              <Button data-testid="counter-offer-btn" size="sm" variant="outline"
                className="rounded-sm border-[#2A2A2A] bg-transparent hover:bg-white/5"
                style={{ color: accent }}>
                <IndianRupee className="w-4 h-4 mr-1" /> Counter-offer
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#0A0A0A] border-[#2A2A2A]">
              <DialogHeader><DialogTitle className="font-display font-black">Send a counter-offer</DialogTitle></DialogHeader>
              <div>
                <Label className="overline text-zinc-400">New price (₹)</Label>
                <Input type="number" value={counter} onChange={(e) => setCounter(e.target.value)}
                  data-testid="counter-price-input"
                  className="mt-2 h-11 bg-[#141414] border-[#2A2A2A] focus-visible:ring-[#FF5A00] rounded-sm" />
              </div>
              <DialogFooter>
                <Button onClick={sendCounter} data-testid="counter-send-btn"
                  className="rounded-sm text-black font-bold" style={{ backgroundColor: accent }}>Send counter</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-2 font-mono-ll text-sm" data-testid="chat-messages">
        {messages.length === 0 && (
          <div className="text-center text-zinc-500 py-8">Start the conversation to lock in the deal.</div>
        )}
        {messages.map((m) => {
          const mine = m.from_user_id === user?.id;
          const isCounter = m.kind === "counter";
          return (
            <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[75%] p-3 border rounded-sm ${
                isCounter ? "bg-[#FF5A00]/15 border-[#FF5A00]/50 text-[#FF5A00]" : "bg-[#0A0A0A] border-[#2A2A2A]"
              }`}>
                <div className="overline text-zinc-500 mb-1">
                  {mine ? "You" : m.from_role === "sender" ? "Sender" : m.from_role === "driver" ? "Driver" : "Lorry Owner"}
                </div>
                <div className="whitespace-pre-wrap">{m.text}</div>
                <div className="mt-1 text-[10px] text-zinc-600">{new Date(m.created_at).toLocaleString("en-IN")}</div>
              </div>
            </div>
          );
        })}
      </div>

      {canParticipate && (
        <form onSubmit={send} className="p-3 border-t border-[#2A2A2A] flex gap-2" data-testid="chat-form">
          <Input value={text} onChange={(e) => setText(e.target.value)} data-testid="chat-input"
            placeholder="Type a message..." className="h-11 bg-[#0A0A0A] border-[#2A2A2A] focus-visible:ring-[#FF5A00] rounded-sm" />
          <Button type="submit" data-testid="chat-send-btn"
            className="rounded-sm text-black font-bold h-11 px-4"
            style={{ backgroundColor: accent }}>
            <Send className="w-4 h-4" />
          </Button>
        </form>
      )}
    </div>
  );
}

function PlaceBidDialog({ loadId, accent, onDone }) {
  const [open, setOpen] = useState(false);
  const [price, setPrice] = useState("");
  const [eta, setEta] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/bids", { load_id: loadId, price_inr: Number(price), eta_hours: Number(eta), note });
      toast.success("Bid placed");
      setOpen(false);
      onDone?.();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="open-place-bid-btn"
          className="mt-6 w-full rounded-sm text-black font-bold h-12"
          style={{ backgroundColor: accent }}>
          <Gavel className="w-4 h-4 mr-2" /> Place a Bid
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-[#0A0A0A] border-[#2A2A2A]">
        <DialogHeader><DialogTitle className="font-display font-black">Place your bid</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4" data-testid="place-bid-form">
          <div>
            <Label className="overline text-zinc-400">Your price (₹)</Label>
            <Input required type="number" value={price} onChange={(e) => setPrice(e.target.value)}
              data-testid="bid-price-input" className="mt-2 h-11 bg-[#141414] border-[#2A2A2A] focus-visible:ring-[#FF5A00] rounded-sm" />
          </div>
          <div>
            <Label className="overline text-zinc-400">ETA / Availability (hours)</Label>
            <Input required type="number" value={eta} onChange={(e) => setEta(e.target.value)}
              data-testid="bid-eta-input" className="mt-2 h-11 bg-[#141414] border-[#2A2A2A] focus-visible:ring-[#FF5A00] rounded-sm" />
          </div>
          <div>
            <Label className="overline text-zinc-400">Note (optional)</Label>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} data-testid="bid-note-input" rows={3}
              className="mt-2 bg-[#141414] border-[#2A2A2A] focus-visible:ring-[#FF5A00] rounded-sm" />
          </div>
          <DialogFooter>
            <Button type="submit" disabled={busy} data-testid="submit-bid-btn"
              className="rounded-sm text-black font-bold h-11 px-6" style={{ backgroundColor: accent }}>
              {busy ? "Placing..." : "Place Bid"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
